/**
 * Warehouse Details
 */

// React
import React, {useEffect, useContext, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';

// Context
import AuthContext from '../../authContext';

// Redux
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Store} from '../../redux/reducers';
import {
  getWarehouseWithIdAction,
  getWarehousesAction,
} from '../warehouses/actions';
import {getUsersAction} from '../users/actions';

// Selector
import {createStructuredSelector} from 'reselect';
import {
  makeSelectWarehouse,
  makeSelectLoading,
  makeSelectError,
  makeSelectList,
} from '../warehouses/selectors';
import {makeSelectUsersList} from '../users/selectors';

//UI
import {makeStyles, createStyles, Theme} from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  CircularProgress,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiKeyOutline,
  mdiArrowRight,
  mdiPhoneOutline,
  mdiCounter,
  mdiCalendar,
  mdiCalendarClock,
  mdiHomeCityOutline,
  mdiMapMarkerOutline,
  mdiWarehouse,
  mdiPlus,
  mdiAccountArrowLeftOutline,
} from '@mdi/js';

// Components
import ListUsers from './list';
import Can from '../../components/Can';

// Rules
import {
  rulesAdminChangeOrderStatus,
  rulesOrderStatus,
} from '../../utils/rulesOrderStatus';

// Helper
import {
  translateOrderStatus,
  formaterPhoneNumber,
  removeEmptyValueObject, useQuery,
} from '../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import {SelectorProps, Props} from './interfaces';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        '@media (max-width: 768px)': {
          margin: theme.spacing(0),
        },
      },
    },
    paper: {
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
      '@media (max-width: 768px)': {
        padding: theme.spacing(0),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(3),
        backgroundColor: theme.palette.background.default,
      },
    },

    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    divider: {
      width: '98%',
      margin: '10px auto',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '10px',
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '28px',
    },
    contentlabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.blue.main,
      },
    },
  })
);

export function Details(props: Props) {

  const classes = useStyles();

  useEffect(() => {
    // @ts-ignore
    getWarehouses();
    props.getUsersDispatch({
      'warehousesId.equals': props.match.params.id,
    });
    props.warehousesDispatch({
      size: 100,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getWarehouses = async () => {
    const {id} = props.match.params;
    await props.getWarehousesWithIdDispatch(id);
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerBox}>
        <Typography variant="h6" component="h2">
          {`
        جزيیات انبار
        ${props.warehouse.name}
     
        `}
        </Typography>
        <span className={classes.contentlabelTitle}>{props.warehouse.id}</span>
      </div>
      <Grid container spacing={6}>
        <Grid xs={6} item container alignItems="center">
          <Button
            aria-label="details"
            size="medium"
            onClick={() => window.history.back()}
          >
            <Icon path={mdiArrowRight} size={1} className={classes.icon} />
            {`بازگشت`}
          </Button>
        </Grid>
      </Grid>

      <Paper elevation={3} className={classes.paper} variant="elevation" square>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiWarehouse} size={1} className={classes.icon} />
                {`نام انبار:  `}
              </span>

              <span>{props.warehouse.name}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiKeyOutline} size={1} className={classes.icon} />
                {`شناسه انبار:  `}
              </span>
              <span>{props.warehouse.id}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiPhoneOutline}
                  size={1}
                  className={classes.icon}
                />
                {` شماره تلفن:  `}
              </span>
              <span>{formaterPhoneNumber(props.warehouse.phoneNumber)}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCounter} size={1} className={classes.icon} />
                {`ظرفیت:  `}
              </span>
              <span>{props.warehouse.capacity}</span>
            </div>
          </Grid>

          <Divider className={classes.divider} />
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCalendar} size={1} className={classes.icon} />
                {`: روزهای فعالیت `}
              </span>
              <span>{props.warehouse.workDays}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiCalendarClock}
                  size={1}
                  className={classes.icon}
                />
                {`: ساعت فعالیت `}
              </span>
              <span>{props.warehouse.workTimes}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiHomeCityOutline}
                  size={1}
                  className={classes.icon}
                />
                {`استان :  `}
              </span>
              <span>{props.warehouse.province}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiHomeCityOutline}
                  size={1}
                  className={classes.icon}
                />
                {`شهر:  `}
              </span>
              <span>{props.warehouse.city}</span>
            </div>
          </Grid>
          {props.warehouse.parentId && (
            <>
              <Divider className={classes.divider} />
              <Grid item xs={12} sm={12}>
                <div className={classes.item}>
                  <span className={classes.label}>
                    <Icon
                      path={mdiHomeCityOutline}
                      size={1}
                      className={classes.icon}
                    />
                    {`والد انبار:  `}
                  </span>
                  <span>
                    {
                      props?.warehouses?.find(
                        item => item?.id === props.warehouse?.parentId
                      )?.name
                    }
                  </span>
                </div>
              </Grid>
            </>
          )}

          <Divider className={classes.divider} />
          <Grid item xs={12} sm={12}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiMapMarkerOutline}
                  size={1}
                  className={classes.icon}
                />
                {`: آدرس `}
              </span>
              <span>{props.warehouse.address}</span>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid container item xs={12} direction="row-reverse">
          <Link
            style={{textDecoration: 'none'}}
            to={`/warehouses/assign-user/${props.warehouse.id}`}
          >
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<Icon path={mdiAccountArrowLeftOutline} size={1} />}
            >
              {`اختصاص کاربر به انبار`}
            </Button>
          </Link>
        </Grid>
        <Grid item xs={12}>
          {props.loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            props.users && <ListUsers data={props.users} />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
  warehouse: makeSelectWarehouse(),
  warehouses: makeSelectList(),
  users: makeSelectUsersList(),
  loading: makeSelectLoading(),
  error: makeSelectError(),
});

function mapDispatchToProps(dispatch: Function) {
  return {
    getWarehousesWithIdDispatch: (id: number) =>
      dispatch(getWarehouseWithIdAction({id})),
    warehousesDispatch: (payload: Object) =>
      dispatch(getWarehousesAction(payload)),
    getUsersDispatch: (payload: object) => dispatch(getUsersAction(payload)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(Details);
